import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import {
  type Note,
  bringNoteBack,
  changeNoteMarkup,
  fetchNotes,
  placeNote,
  removeNoteForGood,
  saveNote,
  strikeNote,
  writeNote,
} from '../api/notes'
import { asFailure, type Failure } from '../api/request'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { hrefFor, navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { markupForSomethingNew } from '../ui/markup'
import { Button } from '../ui/Control'
import { RichText } from '../ui/RichText'
import { Offer } from '../ui/Offer'
import { Done, Failed, Waiting } from '../ui/Status'
import { useSaving } from '../ui/useSaving'
import { Gap, NoteCard } from './notes/NoteCard'

/**
 * The notepad: what you wrote down before you knew where it belonged.
 *
 * The whole area is built around one sentence from the issue — *if I have to open something else first,
 * I do not write it down* — and every decision here follows from it. **One control makes a note**, with
 * no dialog, no title and no folder, and the cursor is in it. It appears at the top. What is typed is
 * saved by itself, a moment after the typing stops.
 *
 * And notes are **provisional**: striking one through draws it through and moves it to the end, "Put
 * back" stands right beside it, and four days later it is gone for good. That is what keeps the pad a pad
 * rather than a second archive nobody tidies.
 *
 * The order is the person's own. It is made by dragging a note into a gap, and equally by two controls on
 * every card — nothing in MyLib may be reachable by gesture alone.
 */
/** The step in the address that means "something is being written that is not a note yet". */
const DRAFT = 'new'

export function Notes() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const openId = route.path[0]
  const announce = useAnnounce()

  const pad = useLoad(useCallback((signal: AbortSignal) => fetchNotes(signal), []))
  const [refused, setRefused] = useState<Failure | null>(null)
  const [dragging, setDragging] = useState(false)

  const notes = pad.load.status === 'ready' ? pad.load.value : []
  /*
   * A note being written that does not exist yet.
   *
   * **Nothing is on the other side until there is something to write down.** A note that says nothing
   * is not a note — the schema refuses one — so "New note" opens this rather than making a row, and
   * the row is written by the first save. That is what makes a blank note impossible rather than
   * merely short-lived: there is no moment at which one exists to be walked away from.
   */
  const draft = openId === DRAFT
  const open = notes.find((note) => note.id === openId)
  const openNotes = notes.filter((note) => note.struckAt === null)

  /*
   * The address can name a note this screen has never heard of.
   *
   * Writing a note from the search does exactly that: the note is made somewhere else and the
   * address is pointed at it. If the pad was already on screen it holds the list from before, the
   * new note is not in it, and nothing at all happens — which is what somebody sees as the control
   * doing nothing.
   *
   * So an id that is not in the list is a reason to ask for the pad again. Once per id: the answer
   * may genuinely not contain it — a note struck on another device — and asking for ever would be a
   * request every render.
   */
  const asked = useRef<string | null>(null)
  useEffect(() => {
    if (openId === undefined || pad.load.status !== 'ready' || open !== undefined) {
      return
    }
    if (asked.current !== openId) {
      asked.current = openId
      pad.retry()
    }
  }, [openId, open, pad])

  const go = (note?: string) => {
    navigate('notes', note === undefined ? [] : [note])
  }

  /** Does something that can be refused, and puts the refusal where it can be read. */
  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  // Straight into the typing, and nothing sent: what is opened is a draft, and the first save is
  // what makes it a note.
  const make = () => {
    setRefused(null)
    go(DRAFT)
  }

  /**
   * Puts a note at [index] among the open ones, counted in the list **without it**.
   *
   * That is what the service means by an index — remove it, then put it back there — and it is what
   * the two controls on a card produce directly.
   */
  const moveTo = (note: string, index: number) => {
    void attempt(async () => {
      await placeNote(note, index)
      pad.retry()
      announce('Moved.')
    })
  }

  /**
   * Puts a note into the gap at [boundary], counted in the list **as it is on the screen**.
   *
   * A dropped note goes *between* two things somebody can see, and the gaps are numbered along the
   * list they are looking at — which is one more thing than the list the service works in, because
   * that one has the dragged note taken out of it first. Every boundary below the note it came from
   * therefore means the same number in both, and every one above it means one less.
   *
   * Without this, dragging a note **downwards** always landed one place too far: dropped between the
   * second and the third, it came to rest after the third.
   */
  const dropInto = (note: string, boundary: number) => {
    const from = openNotes.findIndex((one) => one.id === note)
    moveTo(note, from >= 0 && boundary > from ? boundary - 1 : boundary)
  }

  return (
    <Lanes
      overview={{
        label: 'Notes',
        content: (
          <div className="pad">
            <header className="pad__head">
              <h2 className="pad__title">Notes</h2>
              {notes.length > 0 && (
                <Button tone="primary" onClick={make}>
                  New note
                </Button>
              )}
            </header>

            {refused !== null && <Failed failure={refused} />}

            {pad.load.status === 'waiting' && <Waiting label="Fetching your notes." />}
            {pad.load.status === 'failed' && (
              <Failed
                failure={pad.load.failure}
                onRetry={pad.retry}
                title="The notepad did not load"
              />
            )}

            {pad.load.status === 'ready' && notes.length === 0 && (
              <Offer
                title="Your notepad"
                lead="A sentence you want to keep, a section number, a question to ask next week — written down in the two seconds you have, without deciding where it belongs. Notes stay short on purpose: strike one out when it has done its job, and it is gone four days later."
                action={
                  <Button tone="primary" onClick={make}>
                    New note
                  </Button>
                }
              />
            )}

            {notes.length > 0 && (
              <ul className="notes">
                <Gap index={0} active={dragging} onDrop={dropInto} />
                {notes.map((note) => {
                  const place = openNotes.indexOf(note)
                  return (
                    <Fragment key={note.id}>
                      <NoteCard
                        note={note}
                        open={note.id === openId}
                        onOpen={() => {
                          go(note.id)
                        }}
                        onUp={
                          place > 0
                            ? () => {
                                moveTo(note.id, place - 1)
                              }
                            : null
                        }
                        onDown={
                          place >= 0 && place < openNotes.length - 1
                            ? () => {
                                moveTo(note.id, place + 1)
                              }
                            : null
                        }
                        onStrike={() => {
                          void attempt(async () => {
                            await strikeNote(note.id)
                            pad.retry()
                            if (note.id === openId) {
                              go()
                            }
                            announce('Struck out. It is gone in four days.')
                          })
                        }}
                        onBringBack={() => {
                          void attempt(async () => {
                            await bringNoteBack(note.id)
                            pad.retry()
                            announce('Back on the pad.')
                          })
                        }}
                        onRemove={() => {
                          void attempt(async () => {
                            await removeNoteForGood(note.id)
                            pad.retry()
                            announce('Deleted.')
                          })
                        }}
                        onDragStart={() => {
                          setDragging(true)
                        }}
                        onDragEnd={() => {
                          setDragging(false)
                        }}
                      />
                      {note.struckAt === null && (
                        <Gap index={place + 1} active={dragging} onDrop={dropInto} />
                      )}
                    </Fragment>
                  )
                })}
              </ul>
            )}
          </div>
        ),
      }}
      detail={
        !draft && open === undefined
          ? null
          : {
              label: 'Note',
              content: (
                <OpenNote
                  // A different note is a different thing being written in, not the same editor with
                  // other words in it: remounting is what makes the cursor start in the new one.
                  key={open?.id ?? DRAFT}
                  note={open ?? null}
                  onSaved={(saved) => {
                    pad.retry()
                    /*
                      The draft has become a note, so the address follows it — replacing rather than
                      adding, because nobody wants a back button that leads to a note that never was.
                      **Only if the draft is still what is open.** A save started on the way out
                      finishes after the leaving, and following it then put the note straight back on
                      the screen: Escape looked as though it had done nothing, and it took a second
                      one to get away.
                    */
                    if (open === undefined && window.location.hash === hrefFor('notes', [DRAFT])) {
                      navigate('notes', [saved.id], { replace: true })
                      announce('New note.')
                    }
                  }}
                  onClose={() => {
                    go()
                  }}
                  onGiveUp={() => {
                    go()
                    // Nothing was ever written, so for a draft there is nothing at all to undo — and
                    // for a note that has been emptied out, the note is what goes.
                    if (open !== undefined) {
                      void attempt(async () => {
                        await removeNoteForGood(open.id)
                        pad.retry()
                      })
                    }
                  }}
                />
              ),
            }
      }
    />
  )
}

/**
 * One note, open for writing.
 *
 * It holds what has been typed itself and hands it to the service a moment after the typing stops — see
 * `useSaving`. What is on the screen is therefore never waiting for a request, which is the difference
 * between a notepad and a form.
 *
 * A failed save is **said at the note and does not throw the text away**. That is the whole of what this
 * issue owes the one about writing without a connection: the buffer that survives a broken connection is
 * its business, and it goes in front of `saveNote` rather than in here.
 */
function OpenNote({
  note,
  onSaved,
  onClose,
  onGiveUp,
}: {
  /** `null` for a draft: something being written that is not a note yet. */
  readonly note: Note | null
  readonly onSaved: (saved: Note) => void
  readonly onClose: () => void
  /** Called instead of closing when nothing was ever written into it. */
  readonly onGiveUp: () => void
}) {
  const [text, setText] = useState(note?.text ?? '')
  /**
   * Which markup this is written in.
   *
   * Held here because a draft has no row to hold it: the language is chosen before there is a note,
   * and it goes to the service with the request that makes one. Once the note exists it is the note's
   * own, and changing it is a conversion like a document's.
   */
  const [format, setFormat] = useState(note?.format ?? markupForSomethingNew())
  const [saved, setSaved] = useState<number | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  /** What this is once the draft has been written down. Held here, because the address follows later. */
  const written = useRef<Note | null>(note)

  const saving = useSaving(async (wanted) => {
    // Nothing is sent for a text that says nothing: a note that says nothing is not a note, so a
    // draft simply stays a draft and an emptied note is left as it was until it is closed.
    if (wanted.trim() === '') {
      return
    }
    try {
      const holding = written.current
      const result =
        holding === null ? await writeNote(wanted, format) : await saveNote(holding.id, wanted)
      written.current = result
      setSaved(Date.now())
      setFailure(null)
      onSaved(result)
    } catch (error) {
      // The text stays exactly where it is. Nothing typed is ever thrown away because a request failed.
      setFailure(asFailure(error))
    }
  })

  /**
   * Closing — and, where nothing was ever written, going altogether.
   *
   * A note is made the moment somebody asks for one, before a single character has been typed,
   * because that is the whole promise of the pad. The price is that asking for one and then thinking
   * better of it leaves a blank card behind, and a pad collecting blank cards is a pad nobody keeps
   * using. So an empty one goes on the way out; there is nothing in it to ask about.
   */
  const leave = () => {
    saving.now()
    if (text.trim() === '') {
      // A draft that was never written into is nothing at all; a note emptied out is one that has
      // stopped saying anything, and a note that says nothing is not a note.
      onGiveUp()
      return
    }
    onClose()
  }

  return (
    <article
      className="sheet sheet--note"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          leave()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close this note"
          onClick={leave}
        />
        <p className="sheet__facts">
          <span>Note</span>
          <span aria-hidden="true">·</span>
          <span>saved as you write</span>
          {saved !== null && <Done key={saved} label="Saved" />}
        </p>
      </header>

      {failure !== null && <Failed failure={failure} />}

      <RichText
        text={text}
        format={format}
        label="Note"
        purpose="note"
        /*
          The pad remembers the choice, because a note is made by typing into it and there is no
          control in front of it that could have asked.
        */
        rememberFormat
        onFormat={async (wanted) => {
          const holding = written.current
          if (holding === null) {
            // Still a draft: there is no row yet, so the choice is simply what the note will be
            // written as the moment there is something in it.
            setFormat(wanted)
            return
          }
          const result = await changeNoteMarkup(holding.id, wanted)
          written.current = result
          setFormat(result.format)
          onSaved(result)
        }}
        /*
          A note that already says something is opened to be *read* — the drawn text, not the markup
          it is made of — exactly as a document is. A draft has nothing to read, so it opens where the
          typing goes. Either way this is only what happens before anybody has chosen: a choice
          somebody made wins, and the pad's habit is remembered apart from the library's.
        */
        initially={note === null || note.text.trim() === '' ? 'text' : 'preview'}
        autoFocus
        placeholder="Write it down. Nothing else is needed."
        onChange={(wanted) => {
          setText(wanted)
          saving.typed(wanted)
        }}
      />
    </article>
  )
}
